import type { MarketingReport, MediaType, InputMode, CampaignType, GeoRegion } from '../types'

const CACHE_STORAGE_KEY = 'entertainment_marketing_ai_cache'
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours
const MAX_CACHE_ENTRIES = 50

interface CacheEntry {
  report: MarketingReport
  createdAt: number
  expiresAt: number
  cacheKey: string
}

interface CacheStore {
  entries: Record<string, CacheEntry>
}

export interface ReportCacheParams {
  userInput: string
  mediaType: MediaType
  inputMode: InputMode
  campaignType: CampaignType
  regions: GeoRegion[]
}

/**
 * Generate a deterministic cache key from report parameters.
 * Normalizes input to ensure consistent cache hits.
 */
function generateCacheKey(params: ReportCacheParams): string {
  const normalized = {
    input: params.userInput.toLowerCase().trim().replace(/\s+/g, ' '),
    mediaType: params.mediaType,
    inputMode: params.inputMode,
    campaignType: params.campaignType,
    regions: [...params.regions].sort(),
  }

  // Simple hash function for cache key
  const str = JSON.stringify(normalized)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return `report_${Math.abs(hash).toString(36)}`
}

class ReportCacheService {
  private getStore(): CacheStore {
    try {
      const stored = localStorage.getItem(CACHE_STORAGE_KEY)
      if (!stored) return { entries: {} }
      return JSON.parse(stored) as CacheStore
    } catch (error) {
      console.error('Failed to read cache from localStorage:', error)
      return { entries: {} }
    }
  }

  private setStore(store: CacheStore): void {
    try {
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(store))
    } catch (error) {
      console.error('Failed to save cache to localStorage:', error)
      // If storage is full, clear old entries and retry
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.pruneOldEntries(store, 10)
        try {
          localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(store))
        } catch {
          console.error('Failed to save cache even after pruning')
        }
      }
    }
  }

  private pruneOldEntries(store: CacheStore, keepCount: number): void {
    const entries = Object.values(store.entries)
    entries.sort((a, b) => b.createdAt - a.createdAt)

    const toKeep = entries.slice(0, keepCount)
    store.entries = {}
    for (const entry of toKeep) {
      store.entries[entry.cacheKey] = entry
    }
  }

  /**
   * Get a cached report if it exists and hasn't expired.
   */
  getCachedReport(params: ReportCacheParams): MarketingReport | null {
    const cacheKey = generateCacheKey(params)
    const store = this.getStore()
    const entry = store.entries[cacheKey]

    if (!entry) {
      return null
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      // Remove expired entry
      delete store.entries[cacheKey]
      this.setStore(store)
      return null
    }

    console.log(`Cache hit for key: ${cacheKey}`)
    return entry.report
  }

  /**
   * Cache a report with the given parameters.
   */
  cacheReport(params: ReportCacheParams, report: MarketingReport, ttlMs: number = DEFAULT_TTL_MS): void {
    const cacheKey = generateCacheKey(params)
    const store = this.getStore()
    const now = Date.now()

    // Clean up expired entries first
    this.cleanExpired(store)

    // Enforce max entries limit
    const entryCount = Object.keys(store.entries).length
    if (entryCount >= MAX_CACHE_ENTRIES) {
      this.pruneOldEntries(store, MAX_CACHE_ENTRIES - 1)
    }

    store.entries[cacheKey] = {
      report,
      createdAt: now,
      expiresAt: now + ttlMs,
      cacheKey,
    }

    this.setStore(store)
    console.log(`Cached report with key: ${cacheKey}`)
  }

  private cleanExpired(store: CacheStore): void {
    const now = Date.now()
    for (const key of Object.keys(store.entries)) {
      if (store.entries[key].expiresAt < now) {
        delete store.entries[key]
      }
    }
  }

  /**
   * Invalidate a specific cache entry.
   */
  invalidate(params: ReportCacheParams): void {
    const cacheKey = generateCacheKey(params)
    const store = this.getStore()
    delete store.entries[cacheKey]
    this.setStore(store)
  }

  /**
   * Clear all cached reports.
   */
  clearAll(): void {
    localStorage.removeItem(CACHE_STORAGE_KEY)
  }

  /**
   * Get cache statistics.
   */
  getStats(): { entryCount: number; oldestEntry: Date | null; newestEntry: Date | null } {
    const store = this.getStore()
    const entries = Object.values(store.entries)

    if (entries.length === 0) {
      return { entryCount: 0, oldestEntry: null, newestEntry: null }
    }

    const timestamps = entries.map(e => e.createdAt)
    return {
      entryCount: entries.length,
      oldestEntry: new Date(Math.min(...timestamps)),
      newestEntry: new Date(Math.max(...timestamps)),
    }
  }
}

export const reportCacheService = new ReportCacheService()
